import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import db, { getLoyalty, incrementLoyalty, resetLoyalty, LOYALTY_TARGET } from '@/db';
import { getTombstonedIds } from '@/lib/tombstones';
import { isGhostMenuItem } from '@/lib/ghostMenuBlacklist';
import { evaluateDeliveryArea, extractAddressParts } from '@/lib/deliveryArea';
import { geocodeViaPlacesNew } from '@/lib/placesNew';
import { subscribeSyncState, initFirebaseSync } from '@/lib/firebaseSync';
import { fetchDeliveryPaused, subscribeDeliveryPaused } from '@/lib/deliveryPause';
import { formatCurrency } from '@/lib/formatters';
import { calculateDeliveryFee } from '@/lib/geo';
import { generatePixPayload, generateQRCodeSVG, simulatePixPayment } from '@/lib/pix';
import DOMPurify from 'dompurify';
import { createPixPayment, pollPaymentStatus, isPixConfigured } from '@/lib/mercadoPagoPix';
import CardCheckoutBrick from '@/components/CardCheckoutBrick';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { sendWhatsAppWebhook, buildRestaurantWhatsAppLink, getRestaurantPhone, normalizeBrPhone, openWhatsApp } from '@/lib/notifications';
import { notifyOrderReceived } from '@/lib/zapiWhatsApp';
import { logCrossSellEvent } from '@/lib/crossSell';
import { decrementCartBeverages } from '@/lib/stock';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ShoppingCart, Plus, Minus, MapPin, CreditCard, CheckCircle, ArrowLeft, Bike, Clock, Gift, Star, Flame, Sparkles, UtensilsCrossed, ChefHat, X, PauseCircle, Headphones, Loader2, Copy, RefreshCw, ShieldCheck, Users, Image as ImageIcon, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const STEPS = ['menu', 'cart', 'address', 'payment', 'confirmed'];

const TAG_META = {
  'mais-pedido': { label: 'Mais Pedido', icon: Flame, className: 'bg-orange-500 text-white border-orange-600' },
  'prato-do-dia': { label: 'Prato do Dia', icon: Star, className: 'bg-amber-500 text-white border-amber-600' },
  'novo': { label: 'Novo', icon: Sparkles, className: 'bg-emerald-500 text-white border-emerald-600' },
};

/**
 * Hotfix v7.3 — Normaliza arrays vindos do Firebase Realtime DB.
 * O RTDB serializa arrays como objetos `{0: 'a', 1: 'b'}` quando volta
 * via snapshot, quebrando `.includes()` / `.find()` / `.map()`. Esta
 * função aceita array, objeto-com-índices, null/undefined ou string
 * solta e retorna sempre um array seguro (default `fallback`).
 */
function asArray(value, fallback = []) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  if (typeof value === 'string' && value.trim()) return [value];
  return fallback;
}

function MenuItemCard({ item, inCart, onAdd, onDec, onInc, loadedSet, onLoad }) {
  const hasImage = !!item.imageUrl;
  const loaded = loadedSet.has(item.id);
  const tags = asArray(item.tags);
  const visibleTag = tags.find((t) => TAG_META[t]);
  const tag = visibleTag ? TAG_META[visibleTag] : null;
  const TagIcon = tag?.icon;
  return (
    <div data-testid={`menu-item-${item.id}`} className="bg-white border-2 border-zinc-200 rounded-2xl overflow-hidden hover:border-zinc-400 hover:-translate-y-0.5 transition-all">
      <div className="relative w-full aspect-[16/10] bg-zinc-100 overflow-hidden">
        {/* v7.43 — Sem imageUrl: marcador ESTÁTICO limpo (mesmo estilo do
            MenuAdminPage). Sem imagem, NÃO há <img> pra disparar onLoad,
            então a animação de pulse antiga ficava infinita. */}
        {!hasImage && (
          <div
            data-testid={`menu-no-image-${item.id}`}
            className="absolute inset-0 flex items-center justify-center text-zinc-300"
          >
            <ImageIcon size={36} />
          </div>
        )}
        {/* Tem imagem mas ainda carregando: pulse temporário (cessa no onLoad/onError) */}
        {hasImage && !loaded && (
          <div data-testid={`menu-skeleton-${item.id}`} className="absolute inset-0 animate-pulse bg-gradient-to-br from-zinc-200 via-zinc-100 to-zinc-200">
            <div className="absolute inset-0 flex items-center justify-center text-zinc-300">
              <UtensilsCrossed size={36} />
            </div>
          </div>
        )}
        {hasImage && (
          <img
            data-testid={`menu-img-${item.id}`}
            src={item.imageUrl}
            alt={item.name}
            loading="lazy"
            decoding="async"
            onLoad={() => onLoad(item.id)}
            onError={() => onLoad(item.id)}
            className={`w-full h-full object-cover transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
        {tag && (
          <div data-testid={`menu-badge-${item.id}`} className={`absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide border ${tag.className} shadow-md`}>
            {TagIcon && <TagIcon size={12} />} {tag.label}
          </div>
        )}
        {item.servings > 0 && (
          <div
            data-testid={`menu-servings-${item.id}`}
            className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm border border-zinc-200 text-[11px] font-bold text-zinc-800 shadow-md"
            title={`Serve ${item.servings} ${item.servings === 1 ? 'pessoa' : 'pessoas'}`}
          >
            <Users size={11} strokeWidth={2.5} />
            Serve {item.servings}
          </div>
        )}
      </div>
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-zinc-900 text-base break-words">{item.name}</p>
          <p className="text-xs text-zinc-500 mt-0.5 whitespace-pre-line break-words">{item.description}</p>
          <div className="flex items-baseline gap-2 flex-wrap mt-1.5">
            <p className="font-mono text-xl font-black text-zinc-900 tracking-tighter">{formatCurrency(item.price)}</p>
            {item.servings > 1 && item.price > 0 && (
              <p
                data-testid={`menu-price-per-person-${item.id}`}
                className="text-[11px] font-semibold text-emerald-700 tabular-nums"
                title={`Preço dividido por ${item.servings} pessoas`}
              >
                ≈ {formatCurrency(item.price / item.servings)}/pessoa
              </p>
            )}
          </div>
        </div>
        {inCart ? (
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid={`cart-decrease-${item.id}`} onClick={onDec} className="w-10 h-10 rounded-lg border-2 border-zinc-200 flex items-center justify-center hover:border-zinc-400 active:translate-y-[1px] transition-all"><Minus size={16} /></button>
            <span className="font-mono font-bold text-lg w-8 text-center">{inCart.quantity}</span>
            <button data-testid={`cart-increase-${item.id}`} onClick={onInc} className="w-10 h-10 rounded-lg bg-[#2563eb] text-white flex items-center justify-center active:translate-y-[1px] transition-all"><Plus size={16} /></button>
          </div>
        ) : (
          <button data-testid={`add-to-cart-${item.id}`} onClick={onAdd} className="shrink-0 w-11 h-11 rounded-xl bg-[#2563eb] text-white flex items-center justify-center active:translate-y-[1px] transition-all shadow-sm hover:bg-[#1d4ed8]"><Plus size={20} /></button>
        )}
      </div>
    </div>
  );
}

// v7.78 — Persistência do checkout em localStorage (sobrevive a F5, fechamento
// de aba e descarte de memória do iOS — sessionStorage era perdido nesses casos,
// jogando o cliente de volta pro cardápio). TTL de 6h evita carrinho zumbi.
const CHECKOUT_KEY = 'kaven_delivery_checkout_v1';
const CHECKOUT_TTL_MS = 6 * 60 * 60 * 1000;

function readCheckoutSnapshot() {
  try {
    const raw = localStorage.getItem(CHECKOUT_KEY);
    if (raw) {
      const snap = JSON.parse(raw);
      if (snap && typeof snap === 'object' && Date.now() - (snap.savedAt || 0) <= CHECKOUT_TTL_MS) {
        return snap;
      }
      localStorage.removeItem(CHECKOUT_KEY);
      return null;
    }
    // Migração: snapshot legado do sessionStorage (v7.71)
    const cart = JSON.parse(sessionStorage.getItem('kaven_delivery_cart') || '[]');
    const customer = JSON.parse(sessionStorage.getItem('kaven_delivery_customer') || 'null');
    const step = sessionStorage.getItem('kaven_delivery_step') || 'menu';
    if (Array.isArray(cart) && cart.length > 0) return { cart, customer, step, savedAt: Date.now() };
    return null;
  } catch {
    return null;
  }
}

function clearCheckoutSnapshot() {
  try {
    localStorage.removeItem(CHECKOUT_KEY);
    localStorage.removeItem('kaven_delivery_idem_v1');
    sessionStorage.removeItem('kaven_delivery_cart');
    sessionStorage.removeItem('kaven_delivery_step');
    sessionStorage.removeItem('kaven_delivery_customer');
  } catch {}
}

function MenuSkeletonCard() {
  return (
    <div data-testid="menu-skeleton-card" className="bg-white border-2 border-zinc-200 rounded-2xl overflow-hidden">
      <div className="w-full aspect-[16/10] animate-pulse bg-gradient-to-br from-zinc-200 via-zinc-100 to-zinc-200" />
      <div className="p-4 space-y-2">
        <div className="h-4 w-2/3 bg-zinc-200 rounded animate-pulse" />
        <div className="h-3 w-full bg-zinc-100 rounded animate-pulse" />
        <div className="h-6 w-1/3 bg-zinc-200 rounded animate-pulse mt-2" />
      </div>
    </div>
  );
}

export default function DeliveryPage() {
  // v7.78 — Snapshot lido UMA vez no mount; usado pelos initializers abaixo.
  const [checkoutSnap] = useState(() => readCheckoutSnapshot());
  const [step, setStep] = useState(() => {
    // Só restaura 'cart'/'address' (e só com carrinho não-vazio). NÃO
    // restaura 'payment'/'confirmed': dependem de pixData/pixRealData/
    // orderId, que não são persistidos.
    const s = checkoutSnap?.step;
    const hasCart = Array.isArray(checkoutSnap?.cart) && checkoutSnap.cart.length > 0;
    return hasCart && (s === 'cart' || s === 'address') ? s : 'menu';
  });
  const [menuItems, setMenuItems] = useState([]);
  // v7.35 — Set de IDs tombstoneados pra ocultar pratos deletados mesmo se
  // o Dexie ainda tiver eles em cache (race condition do Firebase sync).
  const [tombstoned, setTombstoned] = useState(new Set());

  // v7.41 — Stale-While-Revalidate: NÃO bloqueia o render esperando sync.
  // O cardápio é lido do Dexie (cache local) e exibido imediatamente.
  // O Firebase sync roda em background e atualiza o Dexie; o polling local
  // (a cada 6s) traz os dados frescos pra UI sem reload.
  //
  // `syncReady` é mantido SÓ pra decidir se devemos mostrar skeleton quando
  // o Dexie está vazio (cliente novo, primeira visita). Após primeira sync
  // (ou 9s offline fallback), se ainda estiver vazio, mostramos estado
  // "cardápio vazio" no lugar do skeleton infinito.
  const [syncReady, setSyncReady] = useState(false);
  // `firstFetchDone`: primeira leitura do Dexie concluiu. Usado pra evitar
  // mostrar "cardápio vazio" antes mesmo de ter lido o IndexedDB.
  const [firstFetchDone, setFirstFetchDone] = useState(false);

  // v7.64 — refresh extraído pra useCallback pra ser reutilizado pelo
  // auto-retry do estado vazio e pelo botão "Tentar novamente".
  // v7.77 — Movido pra antes do efeito de syncReady (ver abaixo): o efeito
  // agora chama refreshMenu() no exato momento em que syncReady vira true,
  // fechando a janela de corrida onde syncReady=true mas `menuItems` ainda
  // reflete o snapshot do ultimo poll de 6s (ate 6s desatualizado). Sem
  // isso, "Cardapio indisponivel" podia aparecer mesmo com dados reais ja
  // presentes no Dexie local (ticket #207625).
  const refreshMenu = useCallback(async () => {
    // v7.35 — Lê tombstones JUNTO com menuItems pra filtrar instantaneamente
    // pratos que o admin deletou em outro device (Firebase sync pode ter
    // delay até o listener limpar o Dexie local).
    const [items, tombs] = await Promise.all([
      db.menuItems.toArray(),
      getTombstonedIds('menuItems').catch(() => new Set()),
    ]);
    setMenuItems(items);
    setTombstoned(tombs);
    setFirstFetchDone(true);
    // v7.98 — `deliveryPaused` NÃO é mais lida do Dexie local aqui: o estado
    // vem do servidor + Firebase (subscribeDeliveryPaused). A leitura local
    // sobrescrevia o valor global a cada poll de 6s.
    db.config.get('activePreset').then((c) => setActivePreset(c?.value || 'Padrão'));
    db.neighborhoods.toArray().then((nbs) => setNeighborhoods(nbs.filter((n) => n.active !== false).sort((a, b) => a.name.localeCompare(b.name))));
  }, []);

  useEffect(() => {
    let done = false;
    const fallback = setTimeout(() => {
      if (!done) {
        done = true;
        setSyncReady(true);
        refreshMenu();
      }
    }, 9000);
    const unsub = subscribeSyncState((state) => {
      const receivedMenu = state?.lastSync?.menuItems?.receive || 0;
      const offline = !state?.initialized;
      if (!done && (receivedMenu > 0 || offline)) {
        done = true;
        clearTimeout(fallback);
        setSyncReady(true);
        refreshMenu();
      }
    });
    return () => { clearTimeout(fallback); unsub?.(); };
  }, [refreshMenu]);
  const [imgLoaded, setImgLoaded] = useState(new Set());
  const [cart, setCart] = useState(() => (Array.isArray(checkoutSnap?.cart) ? checkoutSnap.cart : []));
  const [customer, setCustomer] = useState(() => {
    const empty = { name: '', phone: '', neighborhood: '', address: '', landmark: '' };
    const c = checkoutSnap?.customer;
    return c && typeof c === 'object' ? { ...empty, ...c } : empty;
  });

  // v7.78 — Snapshot contínuo do checkout no localStorage (sobrevive F5/aba).
  useEffect(() => {
    try {
      localStorage.setItem(CHECKOUT_KEY, JSON.stringify({ cart, customer, step, savedAt: Date.now() }));
    } catch {}
  }, [cart, customer, step]);

  // TICKET 207625 - INSTRUMENTACAO (somente log, sem mudanca de comportamento).
  // Captura o gatilho do "checkout reinicia sozinho e volta pro cardapio":
  // quando a pagina remonta/recarrega durante o pagamento, o snapshot ainda
  // trazia step='payment'/'confirmed', mas o initializer rebaixa pra 'menu'.
  // Este beacon marca o evento exato + a causa proxima (ultimo erro).
  useEffect(() => {
    try {
      const snapStep = checkoutSnap?.step;
      const snapHadCart = Array.isArray(checkoutSnap?.cart) && checkoutSnap.cart.length > 0;
      const ageMs = Date.now() - (checkoutSnap?.savedAt || 0);
      let lastErr = 'none';
      try { lastErr = localStorage.getItem('kaven_last_error') || 'none'; } catch {}
      console.log('[CHECKOUT_MOUNT 207625] step=%s snapStep=%s hadCart=%s ageMs=%s', step, snapStep, snapHadCart, ageMs);
      if (step === 'menu' && snapHadCart && (snapStep === 'payment' || snapStep === 'confirmed') && ageMs < 180000) {
        console.warn('[CHECKOUT_INTERRUPTED 207625] remontou/recarregou durante pagamento e caiu pro menu. snapStep=%s ageMs=%s lastError=%s snapshot=%o', snapStep, ageMs, lastErr, checkoutSnap);
        try {
          const _u = (process.env.REACT_APP_BACKEND_URL || '') + '/api/diag/checkout';
          const _b = new Blob([JSON.stringify({ event: 'checkout_interrupted', snapStep, ageMs, lastError: lastErr, ts: new Date().toISOString() })], { type: 'application/json' });
          if (typeof navigator !== 'undefined' && navigator.sendBeacon) navigator.sendBeacon(_u, _b);
        } catch {}
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryDistance, setDeliveryDistance] = useState(0);
  // v7.54 — Status da área de atendimento detectada pelo Google:
  //   null               = ainda não detectou (cliente não selecionou endereço)
  //   'IN_AREA_MATCHED'  = OK, dentro do raio + bairro cadastrado → cobra taxa do bairro
  //   'IN_AREA_CONFIRM'  = dentro do raio mas bairro novo → cliente confirma via WhatsApp
  //   'OUTSIDE'          = fora do raio → bloqueia checkout
  //   'UNKNOWN'          = sem coords / config faltando → fallback manual
  const [areaStatus, setAreaStatus] = useState(null);
  const [areaInfo, setAreaInfo] = useState(null); // detalhes da última avaliação
  // v7.55 — Status do Google Maps SDK: 'idle' | 'loading' | 'ready_new' |
  // 'ready_legacy' | 'unavailable'. Quando 'unavailable', o cliente digita
  // o endereço manualmente e o checkout libera com mensagem de confirmação
  // via WhatsApp (em vez de bloquear esperando seleção de place).
  const [mapsStatus, setMapsStatus] = useState('idle');
  const [pixData, setPixData] = useState(null);
  const [pixRealData, setPixRealData] = useState(null); // Fase 18 — resposta do Mercado Pago quando configurado
  const [pixRealMode, setPixRealMode] = useState(false); // true quando Access Token MP está configurado
  const [pollAttempt, setPollAttempt] = useState(0); // feedback visual da tentativa de polling
  const [pollError, setPollError] = useState(null); // erro transiente mostrado na UI com botão "Tentar de novo"
  const [paymentStatus, setPaymentStatus] = useState('pending');
  // v7.22 — Método de pagamento escolhido pelo cliente. 'pix' (default) ou 'card'.
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [orderId, setOrderId] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  const [loyalty, setLoyalty] = useState({ orderCount: 0 });
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  const [rewardApplied, setRewardApplied] = useState(false);
  const [paused, setPaused] = useState(false);
  // v7.98 — Pausa GLOBAL em tempo real: backend é a autoridade; Firebase
  // propaga na hora em que o admin clica "Pausar Delivery".
  useEffect(() => {
    fetchDeliveryPaused().then((v) => { if (v !== null) setPaused(v); });
    const unsub = subscribeDeliveryPaused((v) => setPaused(v));
    // v8.12 — topo do funil: registra visualização do cardápio (dedup 30min)
    import('@/lib/menuViewTracker').then(({ trackMenuView }) => trackMenuView()).catch(() => {});
    return unsub;
  }, []);
  const [activePreset, setActivePreset] = useState('Padrão');
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [estimatedMinutes, setEstimatedMinutes] = useState(null);
  const [crossSell, setCrossSell] = useState(null); // {trigger, suggestion}
  const cancelPaymentRef = useRef(null);
  // Fase 18 — guard de idempotência: impede que falhas de rede (retry do polling)
  // ou callbacks tardios criem pedidos duplicados no Dexie.
  const paymentRef = useRef({ paymentId: null, creating: false, orderCreated: false, cancelled: false });
  // v7.80 — Chave de idempotência por FINALIZAÇÃO de compra: mesma chave em
  // re-cliques/retries (o servidor devolve o MESMO pagamento em vez de criar
  // outro). Renovada quando o carrinho muda ou quando o pedido é confirmado.
  const checkoutIdemRef = useRef((() => {
    // TICKET 207625 - restaura a chave de idempotencia persistida SE o carrinho
    // for o mesmo (escopada por assinatura do carrinho): um remount/reload no
    // meio do pagamento reusa a MESMA chave -> backend faz idempotent-hit -> sem 2a cobranca.
    try {
      const raw = localStorage.getItem('kaven_delivery_idem_v1');
      if (!raw) return null;
      const rec = JSON.parse(raw);
      const snapCart = Array.isArray(checkoutSnap?.cart) ? checkoutSnap.cart : [];
      const sig = snapCart.map((c) => `${c.id || c.name}:${c.quantity}`).join('|');
      if (rec && rec.key && rec.cartSig === sig && (Date.now() - (rec.savedAt || 0) < 6 * 60 * 60 * 1000)) return rec.key;
    } catch {}
    return null;
  })());

  useEffect(() => {
    const refresh = refreshMenu;
    refresh();
    // v7.35 — polling reduzido para 6s pra cardápio refletir mudanças do admin mais rápido
    const interval = setInterval(refresh, 6000);
    // v7.35 — Refresh agressivo quando a aba volta ao foco (cliente reabre a página)
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    const saved = localStorage.getItem('kaven_last_customer');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setCustomer((prev) => ({ ...prev, name: data.name || '', phone: data.phone || '', neighborhood: data.neighborhood || '' }));
        setLastOrder(data.lastItems || null);
        if (data.phone) {
          getLoyalty(data.phone).then(setLoyalty);
        }
      } catch { /* ignore */ }
    }
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
    };
  }, [refreshMenu]);

  // v7.64 — Auto-retry do estado vazio: enquanto "cardápio indisponível"
  // estiver visível, re-verifica o Dexie a cada 5s por até 30s pra capturar
  // os dados assim que o Firebase terminar a sync em background.
  // `emptyRetryNonce` reinicia a janela quando o cliente toca em "Tentar novamente".
  const [emptyRetryNonce, setEmptyRetryNonce] = useState(0);
  const [retryingMenu, setRetryingMenu] = useState(false);
  const menuIsEmpty = firstFetchDone && syncReady && menuItems.length === 0;
  useEffect(() => {
    if (!menuIsEmpty) return undefined;
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      refreshMenu();
      if (attempts >= 6) clearInterval(interval); // 6 × 5s = 30s
    }, 5000);
    return () => clearInterval(interval);
  }, [menuIsEmpty, emptyRetryNonce, refreshMenu]);

  const handleRetryMenu = async () => {
    setRetryingMenu(true);
    try {
      // v7.65 — "Tentar novamente" de verdade: restabelece os listeners do
      // Firebase (puxa dados remotos pro Dexie via mesclagem incremental),
      // aguarda a mesclagem assentar e só então relê o cache local. Assim o
      // botão recupera o cardápio mesmo se o Dexie foi apagado antes.
      try { await initFirebaseSync(); } catch { /* offline — segue com leitura local */ }
      await new Promise((resolve) => setTimeout(resolve, 2500));
      await refreshMenu();
    } finally {
      setRetryingMenu(false);
    }
    setEmptyRetryNonce((n) => n + 1); // re-arma a janela de 30s de auto-retry
  };

  // Recalculate loyalty when phone changes
  const handlePhoneChange = async (phone) => {
    setCustomer((prev) => ({ ...prev, phone }));
    if (phone.length >= 8) {
      const l = await getLoyalty(phone);
      setLoyalty(l);
    }
  };

  const addToCart = (item) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.menuItemId === item.id);
      if (existing) return prev.map((c) => c.menuItemId === item.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { menuItemId: item.id, name: item.name, price: item.price, quantity: 1, notes: '' }];
    });
    maybeTriggerCrossSell(item);
  };

  // Cross-Sell: ao adicionar Quentinha/Marmita, sugere item com tag 'sugestao-chef' (se cliente ainda não tem no carrinho)
  const maybeTriggerCrossSell = (triggerItem) => {
    const cat = (triggerItem.category || '').toLowerCase();
    if (!cat.includes('quentinha') && !cat.includes('marmita')) return;
    const suggestion = menuItems.find(
      (m) => m.available !== false
        && m.id !== triggerItem.id
        && asArray(m.tags).includes('sugestao-chef')
        && asArray(m.presets, ['Padrão']).includes(activePreset)
        && !cart.some((c) => c.menuItemId === m.id)
    );
    if (!suggestion) return;
    // Se esse mesmo trigger já mostrou sugestão nesta sessão, evita repetir
    const key = `kaven_crosssell_shown_${triggerItem.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    setCrossSell({ trigger: triggerItem, suggestion });
    // Tracking granular (Fase 5)
    logCrossSellEvent('shown', triggerItem.id, suggestion.id);
  };

  const acceptCrossSell = () => {
    if (!crossSell) return;
    const s = crossSell.suggestion;
    setCart((prev) => {
      const existing = prev.find((c) => c.menuItemId === s.id);
      if (existing) return prev.map((c) => c.menuItemId === s.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { menuItemId: s.id, name: s.name, price: s.price, quantity: 1, notes: '' }];
    });
    toast.success(`${s.name} adicionado!`);
    logCrossSellEvent('accepted', crossSell.trigger.id, s.id);
    setCrossSell(null);
  };

  const declineCrossSell = () => {
    if (crossSell) logCrossSellEvent('declined', crossSell.trigger.id, crossSell.suggestion.id);
    setCrossSell(null);
  };

  const updateCartQty = (menuItemId, delta) => {
    setCart((prev) => prev.map((c) => c.menuItemId === menuItemId ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c).filter((c) => c.quantity > 0));
  };

  const updateCartNotes = (menuItemId, notes) => {
    setCart((prev) => prev.map((c) => c.menuItemId === menuItemId ? { ...c, notes } : c));
  };

  // Loyalty reward: auto-apply 100% off one Quentinha on the 10th order
  const isRewardOrder = loyalty.orderCount >= LOYALTY_TARGET - 1;

  useEffect(() => {
    if (isRewardOrder && cart.length > 0 && !rewardApplied) {
      const quentinha = cart.find((c) => c.name.toLowerCase().includes('quentinha'));
      if (quentinha) {
        setLoyaltyDiscount(quentinha.price);
        setRewardApplied(true);
      } else {
        setLoyaltyDiscount(0);
      }
    } else if (!isRewardOrder) {
      setLoyaltyDiscount(0);
      setRewardApplied(false);
    }
  }, [cart, isRewardOrder, rewardApplied]);

  const subtotal = cart.reduce((s, c) => s + c.price * c.quantity, 0);
  const total = Math.max(0, subtotal - loyaltyDiscount + deliveryFee);
  const cartCount = cart.reduce((s, c) => s + c.quantity, 0);
  const remaining = Math.max(0, LOYALTY_TARGET - (loyalty.orderCount || 0));

  /**
   * v7.54 — Avalia se o endereço escolhido pelo cliente está dentro da
   * área de atendimento via Haversine + fuzzy match de bairro.
   *
   * Quando o cliente seleciona um endereço do Google Places, recebemos
   * `place` com geometry.location E address_components → extraímos
   * coords + bairro → calculamos distância → decidimos status.
   */
  /**
   * v7.56 — Aplica resultado de geocoding/place-select à UI:
   * calcula distância, status (IN_AREA_MATCHED/CONFIRM/OUTSIDE) e fee.
   * Centraliza a lógica entre PlaceAutocompleteElement e geocoding REST.
   */
  const applyResolvedPlace = async ({ lat, lng, components }) => {
    const restLat = (await db.config.get('restaurantLat'))?.value ?? -22.6118426;
    const restLng = (await db.config.get('restaurantLng'))?.value ?? -43.1766153;
    const radiusKm = (await db.config.get('deliveryRadiusKm'))?.value ?? 8.0;

    const parts = extractAddressParts({ address_components: components || [] });
    const evaluation = evaluateDeliveryArea({
      placeLat: lat,
      placeLng: lng,
      placeAddressParts: parts,
      restaurantLat: restLat,
      restaurantLng: restLng,
      deliveryRadiusKm: radiusKm,
      cadastratedNeighborhoods: [],
    });

    setAreaStatus(evaluation.status);
    setAreaInfo(evaluation);
    setDeliveryDistance(evaluation.distanceKm || 0);

    // v7.60 — Removido cadastro de bairros. Fee é SEMPRE calculada
    // pela distância (baseFee + perKm). Bairro é só o label detectado
    // pelo Google (informativo no recibo).
    if (evaluation.status === 'IN_AREA_MATCHED' || evaluation.status === 'IN_AREA_CONFIRM') {
      const detected = evaluation.detectedBairro || parts?.bairro || '';
      setCustomer((c) => ({ ...c, neighborhood: detected }));
      const baseFee = (await db.config.get('deliveryBaseFee'))?.value ?? 5.0;
      const perKm = (await db.config.get('deliveryPerKm'))?.value ?? 2.0;
      const fee = calculateDeliveryFee(evaluation.distanceKm, baseFee, perKm);
      setDeliveryFee(fee);
      setEstimatedMinutes(null);
      return { evaluation, fee };
    } else if (evaluation.status === 'OUTSIDE') {
      setCustomer((c) => ({ ...c, neighborhood: '' }));
      setDeliveryFee(0);
      setEstimatedMinutes(null);
      return { evaluation, fee: 0 };
    }
    return { evaluation, fee: 0 };
  };

  const handleAddressPlaceSelected = async ({ formattedAddress, lat, lng, components }) => {
    setCustomer((c) => ({ ...c, address: formattedAddress }));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setAreaStatus(null);
      setAreaInfo(null);
      return null;
    }
    const result = await applyResolvedPlace({ lat, lng, components });
    return result?.evaluation || null;
  };

  /**
   * v7.56 — Geocoding REST fallback usado quando o cliente digitou o
   * endereço manualmente (sem usar o autocomplete). Resolve o texto livre
   * em lat/lng via Places API (New) e aplica a mesma lógica de área.
   * Retorna `{evaluation, fee}` ou null (endereço não encontrado).
   */
  const resolveTypedAddress = async () => {
    const query = customer.address?.trim();
    if (!query) return null;
    const restLat = (await db.config.get('restaurantLat'))?.value ?? -22.6118426;
    const restLng = (await db.config.get('restaurantLng'))?.value ?? -43.1766153;
    const res = await geocodeViaPlacesNew(query, {
      bias: { lat: restLat, lng: restLng },
      biasRadiusMeters: 15000,
    });
    if (!res.ok || !res.place) return null;
    setCustomer((c) => ({ ...c, address: res.place.formattedAddress }));
    return await applyResolvedPlace({
      lat: res.place.lat,
      lng: res.place.lng,
      components: res.place.components,
    });
  };

  // v7.80 — Carrinho mudou = pedido diferente → nova chave de idempotência.
  useEffect(() => {
    checkoutIdemRef.current = null;
  }, [cart]);

  // v7.72 — Anti duplo-clique no pagamento: UMA cobrança por pedido.
  // Guard síncrono via ref (imune a re-render) + estado pro botão.
  const [creatingPayment, setCreatingPayment] = useState(false);

  const handleProceedToPayment = async () => {
    if (paymentRef.current.creating) return;
    paymentRef.current.creating = true;
    setCreatingPayment(true);
    try {
      await proceedToPaymentInner();
    } finally {
      paymentRef.current.creating = false;
      setCreatingPayment(false);
    }
  };

  const proceedToPaymentInner = async () => {
    if (paused) {
      toast.error('Delivery temporariamente fechado. Tente novamente mais tarde.');
      return;
    }
    // v7.98 — Re-checa no SERVIDOR (autoridade) imediatamente antes de gerar
    // o pagamento: fecha a janela em que o admin acabou de pausar e este
    // aparelho ainda não recebeu o evento em tempo real.
    const freshPaused = await fetchDeliveryPaused();
    if (freshPaused === true) {
      setPaused(true);
      toast.error('Restaurante temporariamente fechado para novos pedidos. Tente novamente mais tarde.');
      return;
    }
    if (!customer.name.trim() || !customer.phone.trim()) {
      toast.error('Preencha nome e telefone');
      return;
    }
    if (!customer.address.trim()) {
      toast.error('Selecione um endereço da lista do Google');
      return;
    }
    // v7.55/v7.56 — Bloqueia checkout SOMENTE se já detectamos OUTSIDE.
    if (areaStatus === 'OUTSIDE') {
      toast.error('Endereço fora da área de atendimento. Por favor, entre em contato pelo WhatsApp.');
      return;
    }
    // v7.58 — fee computado AGORA (não do closure stale). Quando o cliente
    // digita endereço manual, deliveryFee só é setado via setState dentro
    // de resolveTypedAddress — a variável `total` do closure deste render
    // ainda reflete a fee anterior (0). Bug crítico de pagamento: PIX era
    // gerado sem incluir entrega. Agora capturamos o fee retornado pelo
    // resolveTypedAddress e recalculamos o total inline.
    let effectiveFee = deliveryFee;
    let effectiveAreaStatus = areaStatus;
    if (!areaStatus || (areaStatus !== 'IN_AREA_MATCHED' && areaStatus !== 'IN_AREA_CONFIRM')) {
      toast.loading('Validando endereço...', { id: 'geocode-toast' });
      let result = null;
      let timedOut = false;
      try {
        // v7.78 — Timeout DURO na validação inteira (15s). O timeout do
        // geocodeViaPlacesNew (v7.74) cobre só o fetch ao Google; as
        // leituras `db.config.get` do Dexie que rodam antes/depois podem
        // pendurar indefinidamente (versionchange/close no iOS). O race
        // garante que o cliente NUNCA fica preso em "Validando endereço...".
        // v7.83 (ticket 218940) -- 20s, nao 15s. O geocode gasta 7s + 7s de
        // reintento (placesNew v7.82) = 14s no pior caso; somando as leituras
        // do Dexie, o teto de 15s disparava ANTES do reintento terminar e o
        // cliente levava "demorou demais" mesmo com a 2a tentativa a caminho.
        result = await Promise.race([
          resolveTypedAddress(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('VALIDATION_TIMEOUT')), 20000)),
        ]);
      } catch (err) {
        // v7.75 — resolveTypedAddress pode lançar (ex: DatabaseClosedError
        // quando o Dexie fecha por versionchange durante um deploy). Sem
        // este catch, a exceção interrompia a função ANTES do toast.dismiss
        // abaixo, deixando "Validando endereço..." preso para sempre.
        timedOut = err?.message === 'VALIDATION_TIMEOUT';
        // eslint-disable-next-line no-console
        console.warn('[CHECKOUT] resolveTypedAddress falhou:', err?.name || err?.message || err);
      } finally {
        toast.dismiss('geocode-toast');
      }
      if (!result || !result.evaluation) {
        toast.error(timedOut
          ? 'A validação do endereço demorou demais (conexão lenta ou instável). Aguarde alguns segundos e toque novamente em "Ir para Pagamento".'
          : 'Não consegui localizar esse endereço. Verifique e digite novamente (rua, número e bairro), ou recarregue a página se o problema persistir.');
        return;
      }
      effectiveAreaStatus = result.evaluation.status;
      effectiveFee = result.fee;
      if (result.evaluation.status === 'OUTSIDE') {
        toast.error(`Esse endereço está a ${result.evaluation.distanceKm?.toFixed(1)}km — fora da nossa área de ${result.evaluation.deliveryRadiusKm}km.`);
        return;
      }
    }
    // Aqui chegamos só se effectiveAreaStatus ∈ {IN_AREA_MATCHED, IN_AREA_CONFIRM}.
    void effectiveAreaStatus;

    // v7.58 — TOTAL recalculado com a fee correta (não usa closure stale)
    const effectiveTotal = Math.max(0, subtotal - loyaltyDiscount + effectiveFee);
    // Reset de guards ao iniciar novo pagamento (creating fica true — ainda
    // estamos dentro do fluxo protegido pelo handleProceedToPayment).
    paymentRef.current = { paymentId: null, creating: true, orderCreated: false, cancelled: false };
    setPollError(null);
    setPollAttempt(0);

    // v7.78 — race: isPixConfigured lê config do servidor/Dexie e também
    // pode pendurar. Se não responder em 12s, aborta com erro claro (nunca
    // cai pro modo MOCK silenciosamente).
    let realMode;
    try {
      realMode = await Promise.race([
        isPixConfigured(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('CONFIG_TIMEOUT')), 12000)),
      ]);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[CHECKOUT] isPixConfigured falhou:', err?.message || err);
      toast.error('Não foi possível conectar ao servidor de pagamento. Verifique sua conexão e tente novamente.');
      return;
    }
    setPixRealMode(realMode);
    setStep('payment');
    setPaymentStatus('pending');

    // v7.22 — Se método=card, não gera Pix; o CardBrick cuida sozinho.
    if (paymentMethod === 'card') {
      return;
    }

    if (realMode) {
      // === PIX REAL — Mercado Pago ===
      setPixData(null);
      setPixRealData(null);
      paymentRef.current.creating = true;
      // v7.80 — Gera (ou reutiliza) a chave de idempotência desta finalização.
      if (!checkoutIdemRef.current) {
        checkoutIdemRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }
      // TICKET 207625 - persiste a chave (escopada ao carrinho) pra sobreviver a
      // um remount/reload durante o pagamento -> reuso da MESMA chave -> backend
      // faz idempotent-hit (X-Idempotency-Key no MP) -> NUNCA gera 2a cobranca.
      try {
        const _sig = cart.map((c) => `${c.id || c.name}:${c.quantity}`).join('|');
        localStorage.setItem('kaven_delivery_idem_v1', JSON.stringify({ key: checkoutIdemRef.current, cartSig: _sig, savedAt: Date.now() }));
      } catch {}
      try {
        const nbForOrder = neighborhoods.find((n) => n.name === customer.neighborhood) || {};
        const resp = await createPixPayment({
          amount: effectiveTotal,
          description: `Kaven Delivery — ${customer.name.trim()}`,
          customerEmail: `${customer.phone.replace(/\D/g, '')}@kaven.delivery`,
          idempotencyKey: checkoutIdemRef.current,
          // v7.67 — envia o pedido junto com a criação do Pix, pra persistir
          // server-side (orders/awaiting_payment) e sobreviver mesmo se a
          // aba do cliente cair durante os até 5min de espera do Pix.
          order: {
            customerName: customer.name.trim(),
            customerPhone: customer.phone.trim(),
            neighborhood: customer.neighborhood,
            address: customer.address.trim(),
            landmark: customer.landmark.trim(),
            customerLat: nbForOrder?.lat || -22.6128,
            customerLng: nbForOrder?.lng || -43.1635,
            items: cart.map((c) => ({ name: c.name, quantity: c.quantity, price: c.price, total: c.price * c.quantity, notes: c.notes || '' })),
            subtotal,
            loyaltyDiscount: loyaltyDiscount > 0 ? loyaltyDiscount : undefined,
            deliveryFee: effectiveFee,
            total: effectiveTotal,
            deliveryDistance: deliveryDistance.toFixed(1),
            paymentMethod: 'Pix',
          },
        });
        if (paymentRef.current.cancelled) return;
        paymentRef.current.paymentId = resp.id;
        setPixRealData(resp);
        setPaymentStatus('waiting');
        // Inicia polling em background (com resiliência a falhas de conexão)
        // v7.58 — passa fee/total efetivos para evitar stale closure no createOrderOnce
        startRealPolling(resp.id, { fee: effectiveFee, total: effectiveTotal });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[PIX REAL] falhou ao gerar cobrança:', err);
        setPollError(err?.message || 'Não foi possível gerar o Pix. Tente novamente.');
        setPaymentStatus('error');
      } finally {
        // outer handleProceedToPayment finally libera o guard `creating`.
      }
    } else {
      // === MOCK / SANDBOX (compat Fase 1-17) ===
      const pix = generatePixPayload(Date.now(), effectiveTotal);
      setPixData(pix);
      setPixRealData(null);
      setPaymentStatus('pending');
    }
  };

  // Fase 18 — polling real com tolerância a falhas de conexão.
  // createOrderOnce() só roda uma vez (guard em paymentRef.current.orderCreated).
  const startRealPolling = async (paymentId, totals = {}) => {
    setPaymentStatus('waiting');
    setPollError(null);
    try {
      const result = await pollPaymentStatus(paymentId, {
        onTick: ({ attempt }) => {
          if (paymentRef.current.cancelled) return;
          setPollAttempt(attempt);
          // Erros transientes são engolidos pelo módulo (continua tentando).
        },
      });
      if (paymentRef.current.cancelled) return;
      // Aprovado — finaliza UMA única vez (idempotente).
      await createOrderOnce(result?.paidAt || new Date().toISOString(), totals);
    } catch (err) {
      if (paymentRef.current.cancelled) return;
      const msg = err?.message === 'timeout'
        ? 'Tempo esgotado (5 min). Verifique se o Pix foi pago e tente novamente.'
        : err?.message || 'Falha ao confirmar pagamento.';
      setPollError(msg);
      setPaymentStatus('error');
    }
  };

  // Guard de criação única de pedido — mesmo que o polling resolva 2x ou um
  // retry de conexão dispare callbacks duplicados, só cria o pedido 1x.
  const createOrderOnce = async (paidAtIso, opts = {}) => {
    if (paymentRef.current.orderCreated) return;
    paymentRef.current.orderCreated = true;

    setPaymentStatus('confirmed');
    const nb = neighborhoods.find((n) => n.name === customer.neighborhood) || {};
    // v7.22 — método de pagamento dinâmico (Pix ou Cartão MP)
    const isCard = opts.method === 'card';
    const pmLabel = isCard
      ? `Cartão ${opts.brand ? opts.brand.toUpperCase() : ''} ${opts.lastFourDigits ? '••' + opts.lastFourDigits : ''}`.trim()
      : 'Pix';
    const newOrder = {
      customerName: customer.name.trim(),
      customerPhone: customer.phone.trim(),
      neighborhood: customer.neighborhood,
      address: customer.address.trim(),
      landmark: customer.landmark.trim(),
      customerLat: nb?.lat || -22.6128,
      customerLng: nb?.lng || -43.1635,
      items: cart.map((c) => ({ name: c.name, quantity: c.quantity, price: c.price, total: c.price * c.quantity, notes: c.notes || '' })),
      subtotal,
      loyaltyDiscount: loyaltyDiscount > 0 ? loyaltyDiscount : undefined,
      deliveryFee: (typeof opts.fee === 'number' ? opts.fee : deliveryFee),
      total: (typeof opts.total === 'number' ? opts.total : total),
      deliveryDistance: deliveryDistance.toFixed(1),
      status: 'preparing',
      paymentMethod: pmLabel,
      paidAt: paidAtIso,
      mpPaymentId: paymentRef.current.paymentId || opts.paymentId || undefined,
      createdAt: new Date().toISOString(),
    };
    // v7.83 (ticket 218940) -- Dono unico por pagamento.
    //
    // Medido no incidente real (Firebase, pagamento 171186521895): a ponte do
    // backend gravou a linha 34 as 22:25:37.095 e este checkout gravou a 35 as
    // 22:25:38.642 -- 1,5s DEPOIS. A ponte chega primeiro, nao um minuto
    // depois. Por isso a claim tem de vir ANTES do insert: quem reivindicar
    // primeiro grava o pedido, o outro reaproveita a linha do vencedor. Foram
    // esses dois registros que a cozinha imprimiu como #34 e #35.
    const mpId = newOrder.mpPaymentId;

    const findLocalOrderId = async () => {
      try {
        const row = await db.deliveryOrders.where('mpPaymentId').equals(mpId).first();
        return row?.id ?? null;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[CHECKOUT] checagem local de duplicidade falhou:', e?.message || e);
        return null;
      }
    };

    const finishWithExisting = (existingId, origin) => {
      // eslint-disable-next-line no-console
      console.log(`[CHECKOUT] pedido de mpPaymentId=${mpId} ja existe (id=${existingId}, via ${origin}) -- reusando`);
      setOrderId(existingId);
      setStep('confirmed');
      clearCheckoutSnapshot();
      checkoutIdemRef.current = null;
      toast.success('Pagamento confirmado!');
    };

    if (mpId) {
      const localId = await findLocalOrderId();
      if (localId != null) {
        finishWithExisting(localId, 'dexie');
        return;
      }

      let outcome = 'unavailable';
      try {
        const { claimPaymentForOrderCreation } = await import('@/lib/firebaseSync');
        outcome = await claimPaymentForOrderCreation(mpId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[CHECKOUT] claim de criacao indisponivel:', e?.message || e);
      }
      // eslint-disable-next-line no-console
      console.log(`[CHECKOUT] claim de criacao p/ mpPaymentId=${mpId}: ${outcome}`);

      if (outcome === 'lost') {
        // Outro caminho (normalmente a ponte) e o dono deste pagamento e a
        // linha dele esta a caminho. Espera o sync trazer.
        for (let attempt = 0; attempt < 12; attempt += 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 350));
          // eslint-disable-next-line no-await-in-loop
          const arrived = await findLocalOrderId();
          if (arrived != null) {
            finishWithExisting(arrived, 'sync');
            return;
          }
        }
        // Sync lento: pergunta direto ao Firebase antes de desistir.
        try {
          const { findRemoteOrderIdByPayment } = await import('@/lib/firebaseSync');
          const remoteId = await findRemoteOrderIdByPayment(mpId);
          if (remoteId != null) {
            finishWithExisting(remoteId, 'firebase');
            return;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[CHECKOUT] busca remota de pedido falhou:', e?.message || e);
        }
        // Nada apareceu. Cria mesmo assim: um ticket repetido incomoda a
        // cozinha, um pedido pago que nunca aparece perde a venda e o cliente.
        // eslint-disable-next-line no-console
        console.warn(`[CHECKOUT] claim perdida mas nenhum pedido apareceu p/ ${mpId} -- criando por seguranca`);
      }
    }

    const id = await db.deliveryOrders.add(newOrder);
    setOrderId(id);


    await db.kdsQueue.add({
      orderId: id,
      source: 'delivery',
      status: 'pending',
      customerName: customer.name.trim(),
      items: cart.map((c) => ({ name: c.name, quantity: c.quantity, notes: c.notes || '' })),
      createdAt: new Date().toISOString(),
    });

    // Fase 20 (P0) — Baixa automática de estoque de bebidas vendidas via delivery.
    // Bebidas são detectadas pelo nome (match com db.beverages); quentinha/marmita é ignorada.
    decrementCartBeverages(cart).catch(() => {});

    sendWhatsAppWebhook({ ...newOrder, id }, 'confirmed');
    // v7.87 — Pix REAL: o BACKEND envia o aviso ao cliente + alerta da equipe
    // na promoção do pagamento (cobre navegador fechado e claim perdida pra
    // ponte). Frontend só envia nos fluxos sem webhook: mock e cartão.
    const backendNotifies = !!mpId && !String(mpId).startsWith('MOCK') && !isCard;
    if (!backendNotifies) {
      notifyOrderReceived({ ...newOrder, id }).catch((e) => console.warn('[Z-API] notifyOrderReceived:', e));
      import('@/lib/operatorNotifications')
        .then(({ notifyStaffNewOrder }) => notifyStaffNewOrder({ ...newOrder, id }))
        .catch((e) => console.warn('[Z-API] notifyStaffNewOrder:', e));
    }

    const newCount = await incrementLoyalty(customer.phone.trim(), customer.name.trim());
    if (newCount >= LOYALTY_TARGET && loyaltyDiscount > 0) {
      await resetLoyalty(customer.phone.trim());
      setLoyalty({ phone: customer.phone.trim(), name: customer.name.trim(), orderCount: 0 });
    } else {
      setLoyalty((prev) => ({ ...prev, orderCount: newCount }));
    }

    localStorage.setItem('kaven_last_customer', JSON.stringify({
      name: customer.name.trim(),
      phone: customer.phone.trim(),
      neighborhood: customer.neighborhood,
      lastItems: cart.map((c) => ({ name: c.name, quantity: c.quantity })),
    }));

    setStep('confirmed');
    // v7.78 — pedido concluído: limpa o snapshot persistido do checkout.
    clearCheckoutSnapshot();
    // v7.80 — próxima compra = nova chave de idempotência.
    checkoutIdemRef.current = null;
    toast.success('Pagamento confirmado!');
  };

  const handleSimulatePayment = () => {
    setPaymentStatus('processing');
    cancelPaymentRef.current = simulatePixPayment(async (result) => {
      await createOrderOnce(result.paidAt);
    }, 4000);
  };

  const copyPixCode = async () => {
    const code = pixRealData?.qrCodeText || pixData?.qrCodeText || '';
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Código Pix copiado!', { description: 'Cole no app do banco para pagar.' });
    } catch {
      toast.error('Não foi possível copiar. Copie manualmente o código abaixo.');
    }
  };

  const retryPayment = () => {
    setPollError(null);
    if (pixRealMode && paymentRef.current.paymentId) {
      startRealPolling(paymentRef.current.paymentId);
    } else {
      // MOCK — reinicia simulação
      handleSimulatePayment();
    }
  };

  useEffect(() => {
    return () => {
      if (cancelPaymentRef.current) cancelPaymentRef.current();
      // Fase 18 — sinaliza cancelamento p/ polling real (se estiver rodando)
      paymentRef.current.cancelled = true;
    };
  }, []);

  const repeatLastOrder = () => {
    if (!lastOrder) return;
    const newCart = [];
    lastOrder.forEach((lo) => {
      const item = menuItems.find((m) => m.name === lo.name);
      if (item) newCart.push({ menuItemId: item.id, name: item.name, price: item.price, quantity: lo.quantity, notes: '' });
    });
    if (newCart.length > 0) { setCart(newCart); toast.success('Pedido anterior carregado!'); }
  };

  // Fase 11 - Fallback humano: botão "Falar com Atendente"
  // v7.79 — Telefone do restaurante pré-carregado no mount: o handler vira
  // SÍNCRONO e o window.open roda no gesto do usuário (popup blocker do
  // iOS Safari bloqueava o window.open que rodava após um await).
  const [waPhone, setWaPhone] = useState('');
  useEffect(() => {
    getRestaurantPhone().then((p) => setWaPhone(normalizeBrPhone(p))).catch(() => {});
  }, []);

  const openRestaurantWhatsApp = (text) => {
    if (waPhone) {
      openWhatsApp(`https://wa.me/${waPhone}?text=${encodeURIComponent(text)}`);
      return;
    }
    // Fallback: telefone ainda não carregado — resolve async (pode cair no
    // popup blocker; openWhatsApp então navega na mesma aba).
    buildRestaurantWhatsAppLink(text).then(openWhatsApp).catch(() => {});
  };

  const handleTalkToAttendant = () => {
    // Mensagem contextual conforme estado atual do fluxo
    let contextMsg;
    if (step === 'menu') {
      contextMsg = `Olá! Estou navegando no cardápio do Kaven e preciso de ajuda.`;
    } else if (step === 'cart' && cart.length > 0) {
      const itemsTxt = cart.map((c) => `${c.quantity}x ${c.name}`).join(', ');
      contextMsg = `Olá! Preciso falar sobre meu pedido no Kaven (${itemsTxt}). Posso confirmar pelo WhatsApp?`;
    } else if (step === 'address' || step === 'payment') {
      contextMsg = `Olá ${customer.name || ''}! Estou finalizando um pedido no Kaven${customer.neighborhood ? ` para ${customer.neighborhood}` : ''} e preciso de ajuda.`;
    } else {
      contextMsg = 'Olá! Preciso de ajuda com meu pedido no Kaven.';
    }
    openRestaurantWhatsApp(contextMsg);
  };

  // Filtragem por preset ativo (Fase 5): itens fora do preset ficam ocultos
  // v7.14 — useMemo para evitar refiltrar/recategorizar a CADA render
  // (que era disparado por changes em cart, customer, paymentStatus, etc).
  // Para 50+ itens em Android low-end, isso elimina 60-150ms de frame drop
  // por interação.
  const visibleMenuItems = useMemo(() => menuItems.filter((m) => {
    // v7.51 — Blacklist defensiva contra pratos fantasma: bloqueia QUALQUER
    // item cuja heurística aponte como seed antigo (nome+price=0+sem foto,
    // ou imageUrl do unsplash seed). Funciona mesmo sem tombstone/stableId.
    if (isGhostMenuItem(m)) return false;
    // v7.35 — Filtra itens tombstoneados (pratos deletados pelo admin que
    // ainda não foram limpos do Dexie local pelo listener do Firebase).
    if (tombstoned.has(Number(m.id))) return false;
    try {
      return asArray(m.presets, ['Padrão']).includes(activePreset);
    } catch {
      return false;
    }
  }), [menuItems, activePreset, tombstoned]);
  const categories = useMemo(
    () => [...new Set(visibleMenuItems.map((m) => m.category).filter(Boolean))],
    [visibleMenuItems],
  );
  // Itens por categoria memoizados — usado no map() do JSX abaixo.
  // Ordena por sortOrder (definido no painel admin) e depois por nome.
  const itemsByCategory = useMemo(() => {
    const map = {};
    for (const m of visibleMenuItems) {
      if (!m.available) continue;
      const cat = m.category || 'Outros';
      if (!map[cat]) map[cat] = [];
      map[cat].push(m);
    }
    for (const cat of Object.keys(map)) {
      map[cat].sort((a, b) => ((a.sortOrder ?? 9999) - (b.sortOrder ?? 9999)) || String(a.name || '').localeCompare(String(b.name || '')));
    }
    return map;
  }, [visibleMenuItems]);

  return (
    <div data-testid="delivery-page" className="min-h-screen bg-[#F4F4F5]">
      {/* Header */}
      <div className="bg-white border-b-2 border-zinc-200 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          {step !== 'menu' && step !== 'confirmed' && (
            <button data-testid="delivery-back-btn" onClick={() => setStep(STEPS[Math.max(0, STEPS.indexOf(step) - 1)])} className="p-2 rounded-lg border-2 border-zinc-200 hover:border-zinc-400 transition-all min-w-[40px] min-h-[40px] flex items-center justify-center">
              <ArrowLeft size={18} />
            </button>
          )}
          <div>
            <h1 className="font-heading text-xl font-black text-zinc-900">KAVEN</h1>
            <p className="text-xs text-zinc-500">Delivery Piabeta/Mage</p>
          </div>
        </div>
        {step === 'menu' && cartCount > 0 && (
          <button data-testid="go-to-cart-btn" onClick={() => setStep('cart')} className="relative bg-[#2563eb] text-white rounded-xl font-bold px-5 py-3 flex items-center gap-2 active:translate-y-[1px] transition-all min-h-[44px]">
            <ShoppingCart size={18} />
            <span>{formatCurrency(subtotal)}</span>
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">{cartCount}</span>
          </button>
        )}
      </div>

      {/* Fase 11 - Fallback Humano (flutuante, sempre visível exceto após confirmação)
          v7.38 — UX: compactado (era pill grande com ícone XL).
          - Padding reduzido (pl-3/pr-5/py-3 → pl-2.5/pr-3.5/py-2)
          - Ícone container 32px → 22px; ícone 16px → 12px
          - Texto bold sm → semibold xs
          - Margens 5 (1.25rem) → bottom-4 right-4 + safe-area pra notch iPhone
          - Shadow reduzida pra parecer shortcut discreto, não CTA principal */}
      {step !== 'confirmed' && (
        <button
          data-testid="talk-to-attendant-btn"
          onClick={handleTalkToAttendant}
          className="fixed z-40 flex items-center gap-1.5 bg-[#25D366] text-white rounded-full pl-2.5 pr-3.5 py-2 shadow-md hover:bg-[#1ebe57] active:translate-y-[1px] transition-all font-semibold text-xs"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
            right: 'calc(env(safe-area-inset-right, 0px) + 16px)',
          }}
          aria-label="Falar com atendente via WhatsApp"
        >
          <span className="w-[22px] h-[22px] rounded-full bg-white/25 flex items-center justify-center">
            <Headphones size={12} strokeWidth={2.25} />
          </span>
          Atendente
        </button>
      )}

      <div className="max-w-2xl mx-auto p-6">
        {/* Paused Banner (Fase 4) */}
        {paused && (
          <div data-testid="paused-banner" className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-5 flex items-start gap-3">
            <PauseCircle size={24} className="text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-heading font-bold text-red-800">Estamos fechados temporariamente</p>
              <p className="text-sm text-red-700 mt-0.5">Você pode navegar pelo cardápio, mas novos pedidos estão pausados. Volte em breve!</p>
            </div>
          </div>
        )}

        {/* STEP: MENU */}
        {step === 'menu' && (
          <div data-testid="delivery-step-menu">
            {/* v7.93 — Destaque da quentinha com foto real da embalagem */}
            <div data-testid="quentinha-highlight" className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 my-3 mb-6 text-center shadow-md">
              <div className="mb-3 flex justify-center">
                <img
                  src="/images/quentinha-kaven.jpg"
                  alt="Nossa Quentinha Kaven"
                  loading="lazy"
                  className="w-full max-w-xs h-48 object-cover rounded-lg shadow-md border border-amber-300"
                />
              </div>
              <h2 className="text-lg md:text-lg font-extrabold text-amber-900 tracking-wide uppercase mb-1">
                🍽️ Comida Caseira de Qualidade Incomparável
              </h2>
              <p className="text-base font-bold text-gray-800 my-2">
                Nossa quentinha vai em embalagem de <span className="text-red-600 underline">1 Litro (~1kg)</span>
              </p>
              <p className="text-sm font-semibold text-gray-700 mb-3">
                Serve perfeitamente <span className="bg-amber-200 px-2 py-0.5 rounded text-amber-900">até 2 pessoas</span> que comam moderadamente!
              </p>
              <div className="bg-red-600 text-white font-black text-base py-1.5 px-3 rounded-lg uppercase tracking-wider shadow-inner">
                💡 Economize compartilhando a nossa quentinha!
              </div>
            </div>
            {/* Texto de apresentação do restaurante */}
            <div data-testid="welcome-text" className="mb-6 bg-white border-2 border-zinc-200 rounded-xl p-5">
              <p className="text-sm text-zinc-700 leading-relaxed">
                <span className="font-heading font-bold text-zinc-900">Bem-vindo ao Kaven!</span>{' '}
                Sabores preparados com ingredientes selecionados, tempero na medida certa e o carinho que você merece.
                Nossas refeições são entregues em embalagens premium de <strong>1000ml</strong> — porções fartas com
                aproximadamente <strong>700g a 850g</strong> de comida —, personalizadas e <strong>100% anti-vazamento</strong>,
                garantindo que seu prato chegue quentinho, perfeito e organizado até você. Peça e comprove a diferença!
              </p>
            </div>
            <div className="mb-6">
              <h2 className="font-heading text-2xl font-bold text-zinc-900">Faca seu pedido</h2>
              {customer.name && <p className="text-sm text-zinc-500 mt-1">Ola, {customer.name}!</p>}
              {lastOrder && (
                <button data-testid="repeat-last-order-btn" onClick={repeatLastOrder} className="mt-3 flex items-center gap-2 text-sm font-bold text-[#2563eb] hover:text-[#1d4ed8] transition-colors">
                  <Clock size={14} /> Repetir ultimo pedido
                </button>
              )}
            </div>

            {/* Loyalty Banner */}
            {customer.phone && loyalty.orderCount > 0 && (
              <div data-testid="loyalty-banner" className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Star size={16} className="text-amber-500" />
                  <span className="text-sm font-bold text-amber-800">Programa Fidelidade</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-amber-200 rounded-full h-3 overflow-hidden">
                    <div
                      data-testid="loyalty-progress-bar"
                      className="bg-amber-500 h-full rounded-full transition-all duration-700"
                      style={{ width: `${Math.min(100, ((loyalty.orderCount || 0) / LOYALTY_TARGET) * 100)}%` }}
                    />
                  </div>
                  <span className="font-mono text-sm font-bold text-amber-700">{loyalty.orderCount || 0}/{LOYALTY_TARGET}</span>
                </div>
                <p data-testid="loyalty-message" className="text-xs text-amber-700 mt-2 font-semibold">
                  {remaining <= 0
                    ? 'Parabens! Voce ganhou uma QUENTINHA GRATIS neste pedido!'
                    : `Faltam ${remaining} pedidos para sua quentinha gratis!`}
                </p>
              </div>
            )}

            {/* v7.41 — Stale-While-Revalidate UI:
                 - Se Dexie ainda não foi lido OU está vazio sem sync, mostra Skeleton.
                 - Caso contrário, mostra o cardápio cacheado IMEDIATAMENTE
                   enquanto o Firebase atualiza em background. */}
            {(!firstFetchDone || (visibleMenuItems.length === 0 && !syncReady)) && (
              <div data-testid="menu-loading" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[0, 1, 2, 3, 4, 5].map((i) => <MenuSkeletonCard key={i} />)}
              </div>
            )}

            {/* Estado vazio real (Dexie lido + sync settled + zero itens)
                v7.64 — com botão de retry manual + auto-retry a cada 5s (30s). */}
            {firstFetchDone && syncReady && visibleMenuItems.length === 0 && (
              <div data-testid="menu-empty-state" className="text-center py-12">
                <UtensilsCrossed size={40} className="mx-auto text-zinc-300 mb-3" />
                <p className="text-sm font-bold text-zinc-600">Cardápio indisponível no momento</p>
                <p className="text-xs text-zinc-400 mt-1">Estamos tentando recarregar automaticamente...</p>
                <button
                  data-testid="menu-retry-btn"
                  onClick={handleRetryMenu}
                  disabled={retryingMenu}
                  className="mt-4 inline-flex items-center gap-2 bg-[#2563eb] text-white rounded-xl font-bold px-5 py-3 min-h-[44px] hover:bg-[#1d4ed8] active:translate-y-[1px] transition-all disabled:opacity-60"
                >
                  <RefreshCw size={16} className={retryingMenu ? 'animate-spin' : ''} />
                  {retryingMenu ? 'Verificando...' : 'Tentar novamente'}
                </button>
              </div>
            )}

            {firstFetchDone && visibleMenuItems.length > 0 && categories.map((cat) => (
              <div key={cat} className="mb-8">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-3">{cat}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(itemsByCategory[cat] || []).map((item) => {
                    const inCart = cart.find((c) => c.menuItemId === item.id);
                    return (
                      <MenuItemCard
                        key={item.id}
                        item={item}
                        inCart={inCart}
                        loadedSet={imgLoaded}
                        onLoad={(id) => setImgLoaded((prev) => {
                          if (prev.has(id)) return prev;
                          const next = new Set(prev);
                          next.add(id);
                          return next;
                        })}
                        onAdd={() => addToCart(item)}
                        onDec={() => updateCartQty(item.id, -1)}
                        onInc={() => updateCartQty(item.id, 1)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* STEP: CART */}
        {step === 'cart' && (
          <div data-testid="delivery-step-cart">
            <h2 className="font-heading text-2xl font-bold text-zinc-900 mb-6">Seu Carrinho</h2>
            <div className="space-y-3 mb-6">
              {cart.map((c) => (
                <div key={c.menuItemId} className="bg-white border-2 border-zinc-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1"><p className="font-semibold text-zinc-900">{c.name}</p><p className="font-mono text-sm text-zinc-500">{formatCurrency(c.price)} un.</p></div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateCartQty(c.menuItemId, -1)} className="w-9 h-9 rounded-lg border-2 border-zinc-200 flex items-center justify-center"><Minus size={14} /></button>
                      <span className="font-mono font-bold w-6 text-center">{c.quantity}</span>
                      <button onClick={() => updateCartQty(c.menuItemId, 1)} className="w-9 h-9 rounded-lg border-2 border-zinc-200 flex items-center justify-center"><Plus size={14} /></button>
                    </div>
                    <p className="font-mono font-bold text-zinc-900 w-24 text-right">{formatCurrency(c.price * c.quantity)}</p>
                  </div>
                  <Input
                    data-testid={`cart-notes-${c.menuItemId}`}
                    placeholder="Observacao (ex: sem cebola)"
                    value={c.notes || ''}
                    onChange={(e) => updateCartNotes(c.menuItemId, e.target.value)}
                    className="mt-2 h-9 border border-zinc-200 rounded-lg text-sm text-zinc-600 placeholder:text-zinc-300"
                  />
                </div>
              ))}
            </div>

            {/* Loyalty progress in cart */}
            {customer.phone && (
              <div data-testid="loyalty-cart-info" className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2">
                  <Gift size={16} className="text-amber-600" />
                  {isRewardOrder ? (
                    <p className="text-sm font-bold text-amber-800">
                      QUENTINHA GRATIS aplicada neste pedido!
                    </p>
                  ) : (
                    <p data-testid="loyalty-remaining" className="text-sm font-semibold text-amber-700">
                      Faltam {remaining} pedidos para sua quentinha gratis!
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex-1 bg-amber-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-amber-500 h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, ((loyalty.orderCount || 0) / LOYALTY_TARGET) * 100)}%` }} />
                  </div>
                  <span className="font-mono text-xs font-bold text-amber-700">{loyalty.orderCount || 0}/{LOYALTY_TARGET}</span>
                </div>
              </div>
            )}

            <div className="bg-white border-2 border-zinc-200 rounded-xl p-5">
              <div className="flex justify-between text-sm mb-2"><span className="text-zinc-500">Subtotal</span><span className="font-mono font-bold">{formatCurrency(subtotal)}</span></div>
              {loyaltyDiscount > 0 && (
                <div data-testid="loyalty-discount-line" className="flex justify-between text-sm mb-2">
                  <span className="text-emerald-600 font-bold flex items-center gap-1"><Gift size={12} /> Fidelidade</span>
                  <span className="font-mono font-bold text-emerald-600">-{formatCurrency(loyaltyDiscount)}</span>
                </div>
              )}
              <div className="border-t border-zinc-200 pt-3 flex justify-between">
                <span className="font-bold text-zinc-900">Total</span>
                <span className="font-mono text-xl font-black text-zinc-900">{formatCurrency(subtotal - loyaltyDiscount)}</span>
              </div>
            </div>
            {paused && (
              <div data-testid="cart-paused-notice" className="mt-4 bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-2">
                <PauseCircle size={20} className="text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm font-semibold text-red-800">Estamos fechados no momento — novos pedidos estão temporariamente pausados.</p>
              </div>
            )}
            <button data-testid="proceed-to-address-btn" onClick={() => setStep('address')} disabled={paused || cart.length === 0} className="w-full mt-4 bg-[#2563eb] text-white rounded-xl font-bold py-4 min-h-[52px] hover:bg-[#1d4ed8] active:translate-y-[1px] transition-all disabled:opacity-40 disabled:cursor-not-allowed">
              {paused ? 'Delivery Fechado' : 'Continuar'}
            </button>
          </div>
        )}

        {/* STEP: ADDRESS */}
        {step === 'address' && (
          <div data-testid="delivery-step-address">
            <h2 className="font-heading text-2xl font-bold text-zinc-900 mb-6">Dados de Entrega</h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Nome</label>
                <Input data-testid="customer-name-input" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} placeholder="Seu nome" className="h-12 border-2 border-zinc-200 rounded-xl text-base" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Telefone</label>
                <Input data-testid="customer-phone-input" value={customer.phone} onChange={(e) => handlePhoneChange(e.target.value)} placeholder="(21) 99999-0000" className="h-12 border-2 border-zinc-200 rounded-xl text-base" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Endereço completo</label>
                {/* v7.54 — Cliente apenas digita endereço. Sistema detecta
                    bairro + área via Google Places automaticamente. */}
                <AddressAutocomplete
                  dataTestId="customer-address-input"
                  value={customer.address}
                  onChange={(text) => {
                    setCustomer({ ...customer, address: text });
                    // Reset área quando cliente digita manualmente
                    if (areaStatus) {
                      setAreaStatus(null);
                      setAreaInfo(null);
                      setDeliveryFee(0);
                      setEstimatedMinutes(null);
                    }
                  }}
                  onPlaceSelected={handleAddressPlaceSelected}
                  onStatusChange={setMapsStatus}
                  placeholder="Rua, número, bairro, cidade"
                  className="h-12 border-2 border-zinc-200 rounded-xl text-base pr-9"
                />
                <p className="text-[10px] text-zinc-400 mt-1">
                  {mapsStatus === 'manual'
                    ? '💡 Digite o endereço completo. O sistema valida automaticamente ao continuar.'
                    : mapsStatus === 'ready_new'
                      ? '💡 Selecione da lista do Google. O sistema identifica seu bairro automaticamente.'
                      : '💡 Carregando validação de endereço...'}
                </p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Ponto de Referência (opcional)</label>
                <Input data-testid="customer-landmark-input" value={customer.landmark} onChange={(e) => setCustomer({ ...customer, landmark: e.target.value })} placeholder="Próximo ao mercado, casa azul, portão preto" className="h-12 border-2 border-zinc-200 rounded-xl text-base" />
              </div>

              {/* v7.54 — Resultado da avaliação de área */}
              {areaStatus === 'OUTSIDE' && (
                <div data-testid="area-status-outside" className="bg-rose-50 border-2 border-rose-300 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🙏</span>
                    <div className="flex-1">
                      <div className="font-bold text-rose-900 text-base">Desculpe, este endereço está fora da nossa área de entrega</div>
                      <p className="text-sm text-rose-800 mt-1">
                        {areaInfo?.distanceKm != null && (
                          <>São <strong>{areaInfo.distanceKm.toFixed(1)} km</strong> do restaurante — nossa área cobre até {areaInfo.deliveryRadiusKm} km.<br /></>
                        )}
                        Se acredita que houve um engano, fale conosco pelo WhatsApp que podemos ajudar.
                      </p>
                      <button
                        data-testid="outside-whatsapp-btn"
                        type="button"
                        onClick={() => openRestaurantWhatsApp(`Olá! Gostaria de saber se vocês entregam em ${customer.address}. Vi no site que parece estar fora da área.`)}
                        className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700"
                      >
                        <MessageCircle size={16} /> Falar com atendente
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {areaStatus === 'IN_AREA_CONFIRM' && (
                <div data-testid="area-status-confirm" className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-sm text-emerald-900 mb-2 font-bold">
                    <CheckCircle size={14} /> Endereço dentro da nossa área
                  </div>
                  <div className="flex items-center gap-2 text-sm text-zinc-600 mb-2">
                    <MapPin size={14} /> {deliveryDistance.toFixed(1)} km do restaurante
                    {areaInfo?.detectedBairro && <> · bairro: <strong>{areaInfo.detectedBairro}</strong></>}
                  </div>
                  <div className="flex justify-between"><span className="text-sm text-zinc-500">Taxa de entrega</span><span data-testid="delivery-fee-display" className="font-mono font-bold">{formatCurrency(deliveryFee)}</span></div>
                  {loyaltyDiscount > 0 && <div className="flex justify-between mt-1"><span className="text-sm text-emerald-600 font-bold">Fidelidade</span><span className="font-mono font-bold text-emerald-600">-{formatCurrency(loyaltyDiscount)}</span></div>}
                  <div className="flex justify-between mt-1 border-t border-emerald-200 pt-2"><span className="font-bold">Total</span><span className="font-mono text-lg font-black text-zinc-900">{formatCurrency(total)}</span></div>
                </div>
              )}

              {areaStatus === 'IN_AREA_MATCHED' && customer.neighborhood && (
                <div data-testid="area-status-matched" className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-sm text-emerald-900 mb-2 font-bold">
                    <CheckCircle size={14} /> Bairro detectado: {customer.neighborhood}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-zinc-600 mb-2"><MapPin size={14} /> {deliveryDistance.toFixed(1)} km do restaurante</div>
                  {estimatedMinutes != null && (
                    <div data-testid="estimated-time" className="flex items-center gap-2 text-sm text-zinc-600 mb-2">
                      <Clock size={14} /> Tempo estimado: <span className="font-mono font-bold">{estimatedMinutes} min</span>
                    </div>
                  )}
                  <div className="flex justify-between"><span className="text-sm text-zinc-500">Taxa de entrega</span><span data-testid="delivery-fee-display" className="font-mono font-bold">{formatCurrency(deliveryFee)}</span></div>
                  {loyaltyDiscount > 0 && <div className="flex justify-between mt-1"><span className="text-sm text-emerald-600 font-bold">Fidelidade</span><span className="font-mono font-bold text-emerald-600">-{formatCurrency(loyaltyDiscount)}</span></div>}
                  <div className="flex justify-between mt-1 border-t border-emerald-200 pt-2"><span className="font-bold">Total</span><span className="font-mono text-lg font-black text-zinc-900">{formatCurrency(total)}</span></div>
                </div>
              )}

              {/* v7.22 — Seletor de método de pagamento */}
              <div data-testid="payment-method-selector" className="space-y-2">
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500">Forma de pagamento</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="payment-method-pix"
                    type="button"
                    onClick={() => setPaymentMethod('pix')}
                    className={`flex items-center justify-center gap-2 h-12 rounded-xl border-2 font-bold text-sm transition-all ${
                      paymentMethod === 'pix'
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                        : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400'
                    }`}
                  >
                    <ShieldCheck size={16} /> Pix
                  </button>
                  <button
                    data-testid="payment-method-card"
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`flex items-center justify-center gap-2 h-12 rounded-xl border-2 font-bold text-sm transition-all ${
                      paymentMethod === 'card'
                        ? 'bg-cyan-50 border-cyan-500 text-cyan-700'
                        : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400'
                    }`}
                  >
                    <CreditCard size={16} /> Cartão
                  </button>
                </div>
                <p className="text-[10px] text-zinc-400 text-center">
                  {paymentMethod === 'pix'
                    ? 'Pague pelo QR Code do banco. Aprovação instantânea.'
                    : 'Pague com cartão de crédito (até 6x). Cobrado uma vez só.'}
                </p>
              </div>
            </div>
            <button data-testid="proceed-to-payment-btn" onClick={handleProceedToPayment} disabled={paused || creatingPayment} className="w-full mt-6 bg-[#2563eb] text-white rounded-xl font-bold py-4 min-h-[52px] hover:bg-[#1d4ed8] active:translate-y-[1px] transition-all disabled:opacity-40 disabled:cursor-not-allowed">
              <CreditCard size={18} className="inline mr-2" /> {paused ? 'Delivery Fechado' : (creatingPayment ? 'Gerando pagamento...' : 'Ir para Pagamento')}
            </button>
          </div>
        )}

        {/* STEP: PAYMENT */}
        {step === 'payment' && (
          <div data-testid="delivery-step-payment" className="text-center">
            {/* v7.22 — Cartão de crédito (Mercado Pago Bricks) */}
            {paymentMethod === 'card' && (
              <div data-testid="delivery-card-payment" className="text-left max-w-md mx-auto">
                <h2 className="font-heading text-2xl font-bold text-zinc-900 mb-1 text-center">Pagamento com cartão</h2>
                <p data-testid="card-mp-badge" className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-3 py-1 mb-5 mx-auto block w-fit">
                  <ShieldCheck size={12} /> Cartão Mercado Pago · Produção
                </p>
                <div className="bg-white border-2 border-zinc-200 rounded-2xl p-5 shadow-sm">
                  <CardCheckoutBrick
                    amount={total}
                    description={`Kaven Delivery — ${customer.name.trim()}`}
                    customerEmail={`${customer.phone.replace(/\D/g, '')}@kaven.delivery`}
                    onApproved={(data) => {
                      paymentRef.current.paymentId = data.id;
                      createOrderOnce(data.dateApproved || new Date().toISOString(), {
                        method: 'card',
                        paymentId: data.id,
                        lastFourDigits: data.lastFourDigits,
                        brand: data.paymentMethodId,
                      });
                    }}
                    onError={(err) => {
                      // eslint-disable-next-line no-console
                      console.warn('[CARD] erro:', err);
                    }}
                  />
                </div>
                <button
                  data-testid="card-back-to-pix-btn"
                  onClick={() => { setPaymentMethod('pix'); setStep('address'); }}
                  className="block mx-auto mt-4 text-sm font-bold text-zinc-500 hover:text-zinc-900 transition-colors"
                >
                  ← Voltar e trocar para Pix
                </button>
              </div>
            )}

            {/* === PIX === */}
            {paymentMethod === 'pix' && (
              <>
            <h2 className="font-heading text-2xl font-bold text-zinc-900 mb-1">Pagamento via Pix</h2>
            {pixRealMode ? (
              <p data-testid="pix-real-badge" className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 mb-5">
                <ShieldCheck size={12} /> Pix Mercado Pago · Produção
              </p>
            ) : (
              <p className="text-sm text-zinc-500 mb-5">Escaneie o QR Code ou copie o codigo</p>
            )}

            {/* === Geração inicial: enquanto o MP não respondeu (real mode) === */}
            {pixRealMode && !pixRealData && paymentStatus !== 'error' && (
              <div data-testid="pix-generating" className="bg-white border-2 border-zinc-200 rounded-2xl p-10 inline-block mb-6 min-w-[260px]">
                <Loader2 size={42} className="mx-auto animate-spin text-emerald-600 mb-3" strokeWidth={2} />
                <p className="text-sm font-bold text-zinc-700">Gerando cobrança Pix...</p>
                <p className="text-[11px] text-zinc-500 mt-1">Conectando ao Mercado Pago</p>
              </div>
            )}

            {/* === QR Code real (Mercado Pago) === */}
            {pixRealMode && pixRealData && (
              <div className="bg-white border-2 border-zinc-200 rounded-2xl p-6 inline-block mb-5 shadow-sm">
                {pixRealData.qrCodeBase64 ? (
                  <img
                    data-testid="pix-qr-code-real"
                    src={`data:image/png;base64,${pixRealData.qrCodeBase64}`}
                    alt="QR Code Pix"
                    className="w-[220px] h-[220px] mx-auto"
                  />
                ) : (
                  <div
                    data-testid="pix-qr-code-real"
                    className="w-[220px] h-[220px]"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(generateQRCodeSVG(pixRealData.qrCodeText || 'pix', 220), { USE_PROFILES: { svg: true } }) }}
                  />
                )}
              </div>
            )}

            {/* === QR Code mock (sandbox/mock mode) === */}
            {!pixRealMode && pixData && (
              <div className="bg-white border-2 border-zinc-200 rounded-xl p-8 inline-block mb-6">
                <div data-testid="pix-qr-code" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(generateQRCodeSVG(pixData.qrCodeText, 200), { USE_PROFILES: { svg: true } }) }} />
              </div>
            )}

            <p className="font-mono text-3xl font-black text-zinc-900 tracking-tighter mb-2">{formatCurrency(total)}</p>
            {loyaltyDiscount > 0 && <p data-testid="payment-loyalty-badge" className="text-sm font-bold text-emerald-600 mb-4 flex items-center justify-center gap-1"><Gift size={14} /> Desconto fidelidade: -{formatCurrency(loyaltyDiscount)}</p>}

            {/* === Copia e Cola === */}
            {((pixRealMode && pixRealData?.qrCodeText) || (!pixRealMode && pixData?.qrCodeText)) && (
              <div data-testid="pix-copypaste-wrapper" className="max-w-sm mx-auto mb-5">
                <label className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 block mb-1.5">
                  Pix Copia e Cola
                </label>
                <div className="flex items-stretch gap-2">
                  <code
                    data-testid="pix-copypaste-code"
                    className="flex-1 font-mono text-[11px] text-zinc-700 bg-zinc-100 border border-zinc-200 rounded-lg px-3 py-2.5 truncate text-left"
                    title={pixRealData?.qrCodeText || pixData?.qrCodeText}
                  >
                    {pixRealData?.qrCodeText || pixData?.qrCodeText}
                  </code>
                  <button
                    data-testid="pix-copypaste-btn"
                    onClick={copyPixCode}
                    className="shrink-0 px-4 rounded-lg bg-zinc-900 text-white font-bold text-xs flex items-center gap-1.5 hover:bg-zinc-800 active:translate-y-[1px] transition-all"
                  >
                    <Copy size={14} /> Copiar
                  </button>
                </div>
              </div>
            )}

            {/* === Estados da UI === */}

            {/* MOCK mode: botão de simulação */}
            {!pixRealMode && paymentStatus === 'pending' && (
              <button data-testid="simulate-payment-btn" onClick={handleSimulatePayment} className="w-full bg-emerald-600 text-white rounded-xl font-bold py-4 min-h-[52px] active:translate-y-[1px] transition-all">
                Simular Pagamento Pix
              </button>
            )}

            {/* REAL mode: aguardando pagamento com spinner elegante */}
            {pixRealMode && paymentStatus === 'waiting' && (
              <div data-testid="pix-waiting" className="max-w-sm mx-auto bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl px-5 py-4">
                <div className="flex items-center justify-center gap-3">
                  <div className="relative">
                    <Loader2 size={28} className="animate-spin text-emerald-600" strokeWidth={2.5} />
                    <div className="absolute inset-0 rounded-full border-2 border-emerald-400/30 animate-ping" />
                  </div>
                  <div className="text-left">
                    <p data-testid="pix-waiting-label" className="text-sm font-bold text-emerald-900">Aguardando pagamento...</p>
                    <p className="text-[11px] text-emerald-700/70">
                      {pollAttempt > 0 ? `Verificando (tentativa ${pollAttempt})` : 'Abra o app do seu banco e finalize o Pix'}
                    </p>
                  </div>
                </div>
                <p data-testid="pix-waiting-note" className="mt-2 text-[10px] text-emerald-700/60 text-center">
                  Seu pedido só entra em preparo após a confirmação do pagamento pelo banco.
                </p>
              </div>
            )}

            {/* MOCK mode: processando */}
            {!pixRealMode && paymentStatus === 'processing' && (
              <div data-testid="pix-processing" className="text-amber-600 font-bold animate-pulse">Aguardando confirmacao...</div>
            )}

            {/* Erro transiente / timeout → permite retry sem recriar cobrança */}
            {paymentStatus === 'error' && (
              <div data-testid="pix-error" className="max-w-sm mx-auto bg-rose-50 border-2 border-rose-200 rounded-2xl p-4 text-left">
                <p className="text-sm font-bold text-rose-900">Não foi possível confirmar o pagamento</p>
                <p className="text-[11px] text-rose-700 mt-0.5">{pollError || 'Erro desconhecido'}</p>
                <button
                  data-testid="pix-retry-btn"
                  onClick={retryPayment}
                  className="mt-3 inline-flex items-center gap-1.5 bg-rose-600 text-white rounded-lg font-bold px-4 py-2 text-xs hover:bg-rose-700 active:translate-y-[1px] transition-all"
                >
                  <RefreshCw size={12} /> Tentar novamente
                </button>
              </div>
            )}

            {/* Confirmado */}
            {paymentStatus === 'confirmed' && (
              <div className="flex items-center justify-center gap-2 text-emerald-600 font-bold"><CheckCircle size={20} /> Pago!</div>
            )}
              </>
            )}
          </div>
        )}

        {/* STEP: CONFIRMED */}
        {step === 'confirmed' && (
          <div data-testid="delivery-step-confirmed" className="text-center py-10">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-emerald-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-zinc-900 mb-2">Pedido Confirmado!</h2>
            <p className="text-zinc-500 mb-2">Seu pedido #{orderId} esta sendo preparado</p>
            {loyaltyDiscount > 0 && (
              <Badge data-testid="reward-used-badge" className="bg-amber-100 text-amber-700 border-amber-200 mb-4">
                <Gift size={12} className="mr-1" /> Quentinha gratis resgatada! Contador zerado.
              </Badge>
            )}
            <div data-testid="loyalty-new-count" className="text-xs text-zinc-400 mb-4">
              Pedidos acumulados: {loyalty.orderCount}/{LOYALTY_TARGET}
            </div>
            <div className="bg-white border-2 border-zinc-200 rounded-xl p-5 text-left mb-6 max-w-sm mx-auto">
              {cart.map((c) => (
                <div key={c.menuItemId} className="flex justify-between text-sm py-1">
                  <span>{c.quantity}x {c.name}</span>
                  <span className="font-mono font-bold">{formatCurrency(c.price * c.quantity)}</span>
                </div>
              ))}
              {loyaltyDiscount > 0 && <div className="flex justify-between text-sm py-1 text-emerald-600"><span>Fidelidade</span><span className="font-mono font-bold">-{formatCurrency(loyaltyDiscount)}</span></div>}
              <div className="border-t border-zinc-200 mt-2 pt-2 flex justify-between text-sm"><span>Entrega</span><span className="font-mono">{formatCurrency(deliveryFee)}</span></div>
              <div className="flex justify-between font-bold mt-1"><span>Total</span><span className="font-mono">{formatCurrency(total)}</span></div>
            </div>
            {orderId && (
              <a href={`/rastreio/${orderId}`} data-testid="track-order-link" className="inline-flex items-center gap-2 bg-[#2563eb] text-white rounded-xl font-bold px-8 py-4 min-h-[52px] active:translate-y-[1px] transition-all">
                <Bike size={18} /> Acompanhar Entrega
              </a>
            )}
            <button data-testid="new-order-btn" onClick={() => { setCart([]); setStep('menu'); setPaymentStatus('pending'); setPixData(null); setPixRealData(null); setPixRealMode(false); setPollError(null); setPollAttempt(0); setOrderId(null); setLoyaltyDiscount(0); setRewardApplied(false); paymentRef.current = { paymentId: null, creating: false, orderCreated: false, cancelled: false }; }} className="block mx-auto mt-4 text-sm font-bold text-zinc-500 hover:text-zinc-900 transition-colors">
              Fazer novo pedido
            </button>
          </div>
        )}
      </div>

      {/* Cross-Sell Dialog (Fase 4) */}
      <Dialog open={!!crossSell} onOpenChange={(open) => { if (!open) declineCrossSell(); }}>
        <DialogContent data-testid="crosssell-dialog" className="max-w-md bg-white border-2 border-zinc-900 rounded-2xl p-0 overflow-hidden">
          <div className="relative">
            {crossSell?.suggestion?.imageUrl ? (
              <img src={crossSell.suggestion.imageUrl} alt={crossSell.suggestion.name} className="w-full aspect-[16/10] object-cover" />
            ) : (
              <div className="w-full aspect-[16/10] bg-zinc-100 flex items-center justify-center text-zinc-300"><UtensilsCrossed size={48} /></div>
            )}
            <div className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2563eb] text-white text-xs font-bold uppercase tracking-wide shadow-lg">
              <ChefHat size={14} /> Sugestão do Chef
            </div>
            <button onClick={declineCrossSell} className="absolute top-3 right-3 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-all">
              <X size={16} />
            </button>
          </div>
          <div className="p-5">
            <DialogHeader>
              <DialogTitle data-testid="crosssell-title" className="font-heading text-xl font-bold text-zinc-900">
                Que tal acompanhar com {crossSell?.suggestion?.name}?
              </DialogTitle>
              <DialogDescription className="text-sm text-zinc-500 mt-1">
                Combina perfeitamente com sua {crossSell?.trigger?.name}. Aceita?
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center justify-between mt-4 mb-4">
              <span className="text-xs uppercase tracking-wider font-bold text-zinc-400">Apenas</span>
              <span className="font-mono text-2xl font-black text-zinc-900 tracking-tighter">+{formatCurrency(crossSell?.suggestion?.price || 0)}</span>
            </div>
            <div className="flex gap-2">
              <button data-testid="crosssell-decline-btn" onClick={declineCrossSell} className="flex-1 rounded-xl border-2 border-zinc-300 font-bold text-zinc-600 py-3 hover:border-zinc-900 transition-all min-h-[44px]">
                Não, obrigado
              </button>
              <button data-testid="crosssell-accept-btn" onClick={acceptCrossSell} className="flex-1 rounded-xl bg-[#2563eb] text-white font-bold py-3 hover:bg-[#1d4ed8] transition-all min-h-[44px] flex items-center justify-center gap-2">
                <Plus size={16} /> Adicionar
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
